import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import { THUMBNAIL_WIDTH_PX } from './constants';
import type { PdfDocumentPreviewStatus } from './usePdfViewerRuntime';

import styles from './ThumbnailRail.module.scss';

type ThumbnailRailProps = {
  pdf: PDFDocumentProxy | null;
  numPages: number;
  currentPage: number;
  markedPages: Set<number>;
  status: PdfDocumentPreviewStatus;
  onSelect: (pageNumber: number) => void;
};

export function ThumbnailRail({
  pdf,
  numPages,
  currentPage,
  markedPages,
  status,
  onSelect,
}: ThumbnailRailProps) {
  const railRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  useEffect(() => {
    const rail = railRef.current;
    const item = itemRefs.current.get(currentPage);
    if (!rail || !item) {
      return;
    }
    const delta =
      item.getBoundingClientRect().top - rail.getBoundingClientRect().top;
    rail.scrollTo({
      top: rail.scrollTop + delta - (rail.clientHeight - item.offsetHeight) / 2,
      behavior: 'auto',
    });
  }, [currentPage, numPages]);

  return (
    <aside className={styles['rail']} ref={railRef}>
      {status === 'ready' && pdf
        ? Array.from({ length: numPages }, (_, index) => {
            const pageNumber = index + 1;
            return (
              <button
                className={clsx(styles['rail-item'], {
                  [styles['rail-item--current']]: pageNumber === currentPage,
                  [styles['rail-item--marked']]: markedPages.has(pageNumber),
                })}
                key={pageNumber}
                onClick={() => onSelect(pageNumber)}
                ref={(node) => {
                  if (node) {
                    itemRefs.current.set(pageNumber, node);
                  } else {
                    itemRefs.current.delete(pageNumber);
                  }
                }}
                type="button"
              >
                <ThumbnailCanvas
                  pageNumber={pageNumber}
                  pdf={pdf}
                  railRef={railRef}
                />
                <span className={styles['rail-item__label']}>
                  {pageNumber}
                  {markedPages.has(pageNumber) ? (
                    <GoogleFontIcon
                      className={styles['rail-item__marker']}
                      name="bookmark"
                      solid
                    />
                  ) : null}
                </span>
              </button>
            );
          })
        : null}
    </aside>
  );
}

function ThumbnailCanvas({
  pdf,
  pageNumber,
  railRef,
}: {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  railRef: RefObject<HTMLElement | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    let cancelled = false;
    let renderStarted = false;
    let renderTask: RenderTask | null = null;

    const render = async () => {
      if (renderStarted) {
        return;
      }

      renderStarted = true;
      const page = await pdf.getPage(pageNumber);

      if (cancelled) {
        return;
      }

      const unscaled = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({
        scale: THUMBNAIL_WIDTH_PX / unscaled.width,
      });

      const outputScale = globalThis.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const transform =
        outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0];
      renderTask = page.render({ canvas, viewport, transform });

      try {
        await renderTask.promise;
      } catch {
        if (!cancelled) {
          renderStarted = false;
        }
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          void render();
        }
      },
      {
        root: railRef.current,
        rootMargin: '200px',
      }
    );

    observer.observe(canvas);
    return () => {
      cancelled = true;
      observer.disconnect();
      renderTask?.cancel();
      canvas.width = 0;
      canvas.height = 0;
      canvas.style.removeProperty('width');
      canvas.style.removeProperty('height');
    };
  }, [pageNumber, pdf, railRef]);

  return <canvas className={styles['thumbnail']} ref={canvasRef} />;
}
